import * as React from 'react'
import { RouteComponentProps } from 'react-router-dom'
import NumberIncrementSelector from '../../ui/NumberIncrementSelector'
import Clear from '../../icons/vehicle/Close'
import { IUpdateVehicleAccessAndCapacity, IVehicleObj } from '../../../types'
import UIMessages from '../../../utils/ui-messages'
import EditBottom from '../../vehicles/edit-vehicle/EditBottom'

interface IAccessAndCapacityProps extends RouteComponentProps<{ id: string }> {
  updateVehicleCapacity: (data: IUpdateVehicleAccessAndCapacity) => void
  nextStep: string
  vehicle: IVehicleObj
  isSaved: boolean
  isRequesting: boolean
  setVehicleSavedStateToInitial: () => void
  getVehicleById: (id: string) => void
  currentVehicle: IVehicleObj
}

interface ICapacity {
  keyToElement: number
  amble: number
  wheelchair: number
  cot: number
}

interface IAccessAndCapacityState {
  wheelchair: boolean
  cot: boolean
  capacities: ICapacity[]
  isSaved: boolean
  isSaving: boolean
}

class AccessAndCapacity extends React.Component<IAccessAndCapacityProps, IAccessAndCapacityState> {
  public state = {
    wheelchair: false,
    cot: false,
    capacities: [{ keyToElement: 0, amble: 0, wheelchair: 0, cot: 0 }],
    isSaved: false,
    isSaving: false
  }

  public componentDidMount() {
    this.props.getVehicleById(this.props.match.params.id)
    this.showSavedValue()
  }

  public componentDidUpdate(prevProps: IAccessAndCapacityProps) {
    if (JSON.stringify(prevProps.vehicle) !== JSON.stringify(this.props.vehicle)) {
      this.showSavedValue()
    }
    if (this.props.isSaved) {
      this.props.history.push(`/vehicles/view/${prevProps.vehicle.id}/access-capacity`)
    }
  }

  public componentWillMount() {
    this.props.setVehicleSavedStateToInitial()
  }

  public render() {
    const { isSaved, isSaving } = this.state

    if (!this.props.vehicle) {
      return <div>Wait</div>
    }
    return (
      <div className='pt2'>
        <div className='ml3'>
          <div className='bold'>Access</div>
          <div className='flex items-center fs-14 mt1'>
            <input type='checkbox' checked={this.state.wheelchair} onChange={this.handleToggleWheelchair} />
            <div className='ml1'>Wheelchair</div>
          </div>
          <div className='flex items-center fs-14 mt1'>
            <input type='checkbox' checked={this.state.cot} onChange={this.handleToggleCOT} />
            <div className='ml1'>COT</div>
          </div>
        </div>
        <div className='mt3 ml3'>
          <div className='bold'>Capacity</div>
          <div className='mt1 flex flex-wrap'>
            {this.state.capacities.length > 0 &&
              this.state.capacities.map((option: ICapacity, key: number) => (
                <div className='p2 border-light-grey4 rounded w277 mr2 mb2' key={key}>
                  <div onClick={this.handleRemove(option.keyToElement)} className='cursor--pointer ml-auto flex'>
                    <Clear className='cursor--pointer icon--10x10 ml-auto flex' />
                  </div>
                  <div className='font-light-blue-grey h6'>Option {key + 1}</div>
                  <div className='flex items-center'>
                    <div className='h5'>{UIMessages.vehicle.passengerSeats}</div>
                    <NumberIncrementSelector
                      value={option.amble}
                      onChange={this.handleTick('AMBLE', option.keyToElement)}
                    />
                  </div>
                  {this.state.wheelchair && (
                    <div className='flex items-center'>
                      <div className='h5'>{UIMessages.vehicle.wheelchair}</div>
                      <NumberIncrementSelector
                        value={option.wheelchair}
                        onChange={this.handleTick('WHEELCHAIR', option.keyToElement)}
                      />
                    </div>
                  )}
                  {this.state.cot && (
                    <div className='flex items-center'>
                      <div className='h5'>{UIMessages.vehicle.cot}</div>
                      <NumberIncrementSelector
                        value={option.cot}
                        onChange={this.handleTick('COT', option.keyToElement)}
                      />
                    </div>
                  )}
                </div>
              ))}
          </div>
          <div className={'fs-12 bold cursor--pointer mt3 ml1'} onClick={this.handleAddCapacity}>
            + Add Capacity
          </div>
        </div>

        {isSaved ? (
          <div className='w100 p2 bg-green font-white bold fs-14 center border-box'>
            Vehicle {this.props.vehicle.make} {this.props.vehicle.model} has been successfully updated
          </div>
        ) : (
          <EditBottom
            isSaving={isSaving}
            cancelPath={`/vehicles/view/${this.props.vehicle.id}/access-capacity`}
            onSave={this.handleSubmit}
          />
        )}
      </div>
    )
  }

  private handleSubmit = () => {
    this.props.updateVehicleCapacity({
      id: this.props.match.params.id,
      capacities:
        this.state.capacities.length > 0
          ? this.state.capacities.map(it => {
              const { keyToElement, ...rest } = it
              return rest
            })
          : [{ amble: 0, wheelchair: 0, cot: 0 }]
    })

    this.setState({
      isSaved: true
    })
  }

  private handleAddCapacity = () =>
    this.setState({
      capacities: [
        ...this.state.capacities,
        {
          keyToElement: this.state.capacities.length,
          amble: 0,
          wheelchair: 0,
          cot: 0
        }
      ]
    })

  private handleTick = (type: string, keyToElement: number) => (value: number) => {
    // TODO Find better way to store data
    const newCapacity = this.state.capacities.map(it => {
      if (it.keyToElement === keyToElement) {
        switch (type) {
          case 'AMBLE':
            const { amble, ...withoutPassengerSeats } = it
            return { amble: value, ...withoutPassengerSeats }
          case 'WHEELCHAIR':
            const { wheelchair, ...withoutWheelChair } = it
            return { wheelchair: value, ...withoutWheelChair }
          case 'COT':
            const { cot, ...withoutCOT } = it
            return { cot: value, ...withoutCOT }
          default:
            return it
        }
      }
      return it
    })
    this.setState({
      capacities: newCapacity
    })
  }

  private handleRemove = (keyToElement: number) => () => {
    this.setState({
      capacities: this.state.capacities.filter((it: ICapacity) => it.keyToElement !== keyToElement)
    })
  }

  private handleToggleWheelchair = () => this.setState({ wheelchair: !this.state.wheelchair })

  private handleToggleCOT = () => this.setState({ cot: !this.state.cot })

  private showSavedValue = () => {
    if (!this.props.vehicle || !this.props.vehicle.capacities.length) {
      return
    }
    this.setState({
      capacities: this.props.vehicle.capacities.map((it, i) => ({ keyToElement: i, ...it })),
      wheelchair: this.props.vehicle.capacities.some(it => it.wheelchair > 0),
      cot: this.props.vehicle.capacities.some(it => it.wheelchair > 0)
    })
  }
}

export default AccessAndCapacity



// WEBPACK FOOTER //
// ./src/components/vehicles/edit-vehicle/AccessAndCapacity.tsx